import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Box,
  Pagination,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ChipIcon from '@mui/icons-material/Code';

function Wishlist() {
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const offresParPage = 9;
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/my-wishlist`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération de la wishlist');
        }

        const data = await response.json();
        setOffres(data.wishlist || []);
        setError(null);
      } catch (error) {
        console.error("Erreur lors du chargement de la wishlist:", error);
        setError("Une erreur est survenue lors du chargement de vos favoris");
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchWishlist();
    }
  }, [currentUser]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page')) || 1;
    setPage(pageParam);
  }, [location.search]);

  const handleRemoveFromWishlist = async (offreId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/wishlist/${offreId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du favori');
      }

      setOffres(prevOffres => prevOffres.filter(offre => offre.id_offres !== offreId));
    } catch (error) {
      console.error("Erreur lors de la suppression de l'offre de la wishlist:", error);
      setError("Une erreur est survenue lors de la suppression du favori");
    }
  };

  const handleCardClick = (offreId) => {
    navigate(`/offres/${offreId}`, { 
      state: { 
        from: '/wishlist',
        page: page 
      } 
    });
  };

  // Pagination
  const indexDernierElement = page * offresParPage;
  const indexPremierElement = indexDernierElement - offresParPage;
  const offresAffichees = offres.slice(indexPremierElement, indexDernierElement);
  const nombrePages = Math.ceil(offres.length / offresParPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    navigate(`/wishlist?page=${newPage}`);
    window.scrollTo({
      top: document.getElementById('wishlist-list').offsetTop - 20,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        align="center"
        sx={{
          opacity: 0,
          animation: 'fadeIn 0.8s ease-out forwards',
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}
      >
        Ma Liste de Favoris
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box id="wishlist-list">
        {offres.length === 0 ? (
          <Typography align="center">Aucune offre dans vos favoris</Typography>
        ) : (
          <>
            <Grid container spacing={4} justifyContent="center">
              {offresAffichees.map((offre, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  key={offre.id_offres}
                  sx={{ 
                    maxWidth: '400px',
                    ...(index < 3 ? {
                      opacity: 0,
                      animation: `fadeIn 0.4s ease-out ${0.2 + index * 0.1}s forwards`,
                      '@keyframes fadeIn': {
                        '0%': { opacity: 0, transform: 'translateY(20px)' },
                        '100%': { opacity: 1, transform: 'translateY(0)' }
                      }
                    } : {
                      opacity: window.scrollY > (100 + (index - 3) * 30) ? 1 : 0,
                      transform: `translateY(${window.scrollY > (100 + (index - 3) * 30) ? 0 : 20}px)`,
                      transition: 'all 0.3s ease-out',
                    })
                  }}
                >
                  <Card sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      cursor: 'pointer'
                    }
                  }}>
                    <CardContent sx={{ 
                      flex: 1, 
                      display: 'flex', 
                      flexDirection: 'column' 
                    }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {offre.titre}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <BusinessIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {offre.entreprise?.nom || 'Entreprise non spécifiée'}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {offre.localisation}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <WorkIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Type : {offre.type_stage || 'Stage'}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Durée : {offre.duree || 'Non spécifiée'}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <DateRangeIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Ajouté le : {new Date(offre.dateAjout).toLocaleDateString('fr-FR')}
                          </Typography>
                        </Box>

                        {offre.technologies && offre.technologies.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Technologies :
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {offre.technologies.map((tech) => (
                                <Chip
                                  key={tech.id_techno}
                                  icon={<ChipIcon />}
                                  label={tech.nom_techno}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<FavoriteIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromWishlist(offre.id_offres);
                          }}
                        >
                          Retirer
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => handleCardClick(offre.id_offres)}
                        >
                          Voir l'offre
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {nombrePages > 1 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 4,
                opacity: scrollY > 200 ? 1 : 0,
                transform: `translateY(${scrollY > 200 ? 0 : 30}px)`,
                transition: 'all 0.6s ease-out',
              }}>
                <Pagination 
                  count={nombrePages}
                  page={page}
                  onChange={handleChangePage}
                  color="primary"
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'text.primary',
                    },
                    '& .Mui-selected': {
                      bgcolor: 'primary.main !important',
                      color: 'primary.contrastText !important'
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

export default Wishlist; 